// uploads.ts — "Your uploads" roster: the review doorway. Every capture this
// account has ever uploaded (RLS-scoped ledger), grouped by task, each row
// opening the transcript review view. The editor (verify.ts) reads everything
// from R2 via GET /session/file, so sessions long cleared from the headset are
// fully reviewable here — before this section, the only doorway was the
// admin board's SYNCED·ON HEADSET column, which is empty the moment a capture
// is cleared (i.e. for almost every real session).
import { supabase } from "./auth.ts";
import type { UploadedRow } from "./upload.ts";

const esc = (s: unknown) => String(s).replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

// Task id → name, cached for the page's lifetime. RLS gives contractors the
// active tasks only — sessions against archived/unknown tasks fall into the
// "Other captures" bucket rather than erroring.
let taskNamesP: Promise<Map<string, string>> | null = null;
function taskNames(): Promise<Map<string, string>> {
  taskNamesP ??= supabase.from("tasks").select("id,name")
    .then(({ data }) => new Map((data ?? []).map((t: any) => [t.id as string, t.name as string])));
  return taskNamesP;
}

// Best capture time we can get without touching R2: the ledger's captured_at,
// else the timestamp baked into the session id (a collection clip's id ends in
// one too), else when it was uploaded.
function capturedDate(id: string, r: UploadedRow): Date | null {
  if (r.capturedAt) return new Date(r.capturedAt);
  const m = id.match(/(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})(?!.*\d{8}_\d{6})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  return r.uploadedAt ? new Date(r.uploadedAt) : null;
}
const fmtWhen = (d: Date | null) => d
  ? d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  : "unknown time";

type Row = { id: string; r: UploadedRow; when: Date | null };

function rowHtml({ id, r, when }: Row): string {
  const chips: string[] = [];
  if (r.status === "quarantined") chips.push(`<span class="uchip warn" title="Held back by the upload pipeline — still reviewable.">quarantined</span>`);
  if (r.isTest) chips.push(`<span class="uchip" title="Onboarding/test capture.">test</span>`);
  if (r.qaStatus) chips.push(r.qaStatus === "pass"
    ? `<span class="uchip ok">QA pass</span>`
    : `<span class="uchip err">QA ${esc(r.qaStatus)}</span>`);
  chips.push(r.reviewedAt
    ? `<span class="uchip rev" title="Marked reviewed ${esc(new Date(r.reviewedAt).toLocaleString())}">reviewed ✓</span>`
    : `<span class="uchip needs" title="Open it and press Mark reviewed when the transcript looks right.">needs review</span>`);
  return `<div class="cap clickable" data-review="${esc(id)}" title="Review ${esc(id)}">` +
    `<span class="uwhen">${esc(fmtWhen(when))}</span>` +
    `<span class="cid">${esc(id)}</span>${chips.join("")}` +
    `<span class="cverify">Review →</span></div>`;
}

/**
 * Render the roster into #uploadsList. Re-entrant (the board refresh calls it
 * on every ledger read); open/closed state of the task groups survives.
 */
export async function renderUploads(
  uploaded: Map<string, UploadedRow>,
  onOpen: (id: string) => void,
): Promise<void> {
  const box = document.getElementById("uploadsList");
  const card = document.getElementById("uploadsCard");
  if (!box || !card) return;

  // Roster = everything that actually landed. Mid-flight rows (pending/
  // uploading) belong to the sync board, not here.
  const rows: Row[] = [...uploaded.entries()]
    .filter(([, r]) => r.status === "complete" || r.status === "quarantined")
    .map(([id, r]) => ({ id, r, when: capturedDate(id, r) }));

  if (!rows.length) { card.style.display = "none"; return; }
  card.style.display = "";

  const names = await taskNames();
  // Group by task; unknown/absent task ids share one bucket at the end.
  // "~" can't appear in a uuid, so the sentinel can't collide with a task id.
  const OTHER = "~other";
  const groups = new Map<string, Row[]>();
  for (const row of rows) {
    const key = row.r.taskId && names.has(row.r.taskId) ? row.r.taskId : OTHER;
    let g = groups.get(key);
    if (!g) groups.set(key, g = []);
    g.push(row);
  }
  for (const g of groups.values())
    g.sort((a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0));
  const ordered = [...groups.entries()].sort((a, b) => {
    if (a[0] === OTHER) return 1;                       // "Other captures" last
    if (b[0] === OTHER) return -1;
    return (b[1][0].when?.getTime() ?? 0) - (a[1][0].when?.getTime() ?? 0);
  });

  // Keep the user's expand/collapse choices across re-renders; default:
  // newest group open.
  const openState = new Map<string, boolean>();
  for (const d of box.querySelectorAll<HTMLDetailsElement>("details[data-group]"))
    openState.set(d.dataset.group!, d.open);

  box.innerHTML = ordered.map(([key, g], i) => {
    const label = key === OTHER ? "Other captures" : names.get(key)!;
    const open = openState.get(key) ?? (i === 0);
    const nRev = g.filter((x) => x.r.reviewedAt).length;
    const count = `${g.length} capture${g.length === 1 ? "" : "s"}` +
      (nRev ? ` · ${nRev === g.length ? "all" : nRev} reviewed` : "");
    return `<details class="task" data-group="${esc(key)}"${open ? " open" : ""}>` +
      `<summary><b>${esc(label)}</b>` +
      `<span class="tcount">${count}</span></summary>` +
      g.map(rowHtml).join("") +
      `</details>`;
  }).join("");

  for (const el of box.querySelectorAll<HTMLElement>("[data-review]")) {
    el.addEventListener("click", () => onOpen(el.dataset.review!));
  }

  // The needs-review count on the summary card's "Review your captures" link.
  const need = rows.filter((x) => !x.r.reviewedAt).length;
  const badge = document.getElementById("reviewNeedCount");
  if (badge) {
    badge.style.display = need ? "" : "none";
    badge.textContent = `${need} need${need === 1 ? "s" : ""} review`;
  }
}
